<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import MessengerConnectionsAPI from 'dashboard/api/messengerConnections';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();

const connections = ref([]);
const loading = ref(true);
const loadError = ref(false);
// Uma resposta que chega depois de sair da tela não pode mexer em estado.
let isUnmounted = false;

// A conexão só muda de estado quando o cliente age no painel da Meta ou
// quando um webhook falha — nada disso acontece enquanto ele olha a lista.
// Sem polling, não há temporizador para vazar na saída.
const fetchConnections = async () => {
  loading.value = true;
  try {
    const { data } = await MessengerConnectionsAPI.get();
    if (isUnmounted) return;
    connections.value = data;
    loadError.value = false;
  } catch (error) {
    if (isUnmounted) return;
    // Sem lista antiga para preservar (não há polling): o aviso é a tela.
    loadError.value = true;
  } finally {
    if (!isUnmounted) loading.value = false;
  }
};

const needsReconnect = connection =>
  connection.status === 'reauthorization_required';

const statusLabel = connection =>
  needsReconnect(connection)
    ? t('MESSENGER_CONNECT.STATUS.REAUTHORIZATION_REQUIRED')
    : t('MESSENGER_CONNECT.STATUS.CONNECTED');

const statusClass = connection =>
  needsReconnect(connection) ? 'bg-n-amber-9' : 'bg-n-teal-9';

// A Página não tem um "@" como o Instagram: o que o cliente tem em mãos para
// conferir qual Página é qual (ele pode ter mais de uma) é o número que ele
// mesmo copiou do painel da Meta.
const pageLabel = connection =>
  t('MESSENGER_CONNECT.PAGE_LABEL', { id: connection.page_id });

const goToConnect = () => router.push({ name: 'messenger_connect' });

// Mesma tela de conexão, em modo reconexão: a caixa já existe e só as
// credenciais mudam, então o histórico de conversas fica de pé.
const goToReconnect = inboxId =>
  router.push({ name: 'messenger_connect', query: { inbox_id: inboxId } });

onMounted(fetchConnections);

onUnmounted(() => {
  isUnmounted = true;
});
</script>

<template>
  <div class="w-full h-full p-8 overflow-auto">
    <div class="flex items-start justify-between max-w-3xl mb-6">
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ $t('MESSENGER_CONNECT.TITLE') }}
        </h1>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('MESSENGER_CONNECT.SUBTITLE') }}
        </p>
      </div>
      <NextButton
        solid
        teal
        :label="$t('MESSENGER_CONNECT.CONNECT_BUTTON')"
        @click="goToConnect"
      />
    </div>

    <p v-if="loading" class="text-sm text-n-slate-11">
      {{ $t('MESSENGER_CONNECT.LOADING') }}
    </p>
    <template v-else>
      <div v-if="loadError" class="flex flex-col items-start max-w-3xl gap-3">
        <p class="text-sm text-n-slate-11">
          {{ $t('MESSENGER_CONNECT.LOAD_ERROR') }}
        </p>
        <NextButton
          outline
          slate
          :label="$t('MESSENGER_CONNECT.ACTIONS.RETRY')"
          @click="fetchConnections"
        />
      </div>

      <p v-else-if="!connections.length" class="text-sm text-n-slate-11">
        {{ $t('MESSENGER_CONNECT.EMPTY') }}
      </p>

      <template v-else>
        <ul class="max-w-3xl m-0 list-none">
          <li
            v-for="connection in connections"
            :key="connection.id"
            class="flex items-center justify-between gap-4 px-4 py-3 mb-2 border rounded-lg border-n-weak bg-n-solid-2"
          >
            <div class="min-w-0">
              <p class="font-medium truncate text-n-slate-12">
                {{ connection.name }}
              </p>
              <p class="text-sm truncate text-n-slate-11">
                {{ pageLabel(connection) }}
              </p>
            </div>
            <div class="flex items-center gap-3">
              <span class="flex items-center gap-2 text-sm text-n-slate-11">
                <span
                  class="rounded-full size-2"
                  :class="statusClass(connection)"
                />
                {{ statusLabel(connection) }}
              </span>
              <!-- Em QUALQUER caixa, não só nas marcadas como "precisa
                   reconectar": o aviso de inscrição não confirmada manda
                   reconectar uma caixa que continua com status "conectado" —
                   sem o botão aqui, esse conselho não tinha como ser seguido. -->
              <NextButton
                outline
                teal
                :label="$t('MESSENGER_CONNECT.ACTIONS.RECONNECT')"
                @click="goToReconnect(connection.id)"
              />
            </div>
          </li>
        </ul>

        <!-- Apagar caixa continua onde sempre esteve: esta tela não tem ação
             destrutiva, e o cliente precisa saber para onde ir. -->
        <p class="max-w-3xl mt-4 text-xs text-n-slate-10">
          {{ $t('MESSENGER_CONNECT.DELETE_HINT') }}
        </p>
      </template>
    </template>
  </div>
</template>
